import { useCallback, useEffect, useMemo, useState } from "react";
import { AiOutlineFileSync } from "react-icons/ai";
import { CiCalendar } from "react-icons/ci";
import { FaSitemap } from "react-icons/fa";
import { MdNumbers } from "react-icons/md";

import { BiCollapseVertical, BiExpandVertical } from "react-icons/bi";
import { LuArrowUpToLine } from "react-icons/lu";
import Button from "../../../components/Button";
import Card from "../../../components/Card";
import SortAndFilterSection, {
  SortAndFilterableItem,
} from "../../../components/SortAndFilterSection";
import { useAdminApi } from "../../../hooks/api-hooks/useAdminApi";
import { SensorReference, Site } from "../../../types/rokaTypes";
import { selectableFilterOptions, sortOptions } from "./sitesAdminConfig";
import SensorReferenceField from "./SensorReferenceField";
import SitesAdminForm from "./SitesAdminForm";

const SitesAdmin = () => {
  const { fetchSites, fetchSensorReference } = useAdminApi();
  const [sites, setSites] = useState<Site[]>([]);
  const [expandedSite, setExpandedSite] = useState<string | null>(null);
  const [filteredAndSortedSites, setFilteredAndSortedSites] = useState<Site[]>(
    [],
  );
  const [referenceData, setReferenceData] = useState<
    Record<string, Record<string, SensorReference>>
  >({});

  useEffect(() => {
    fetchSites()
      .then(setSites)
      .catch((error) => {
        console.error("Error fetching sites:", error);
      });
  }, [fetchSites]);

  const expandSite = (site: Site) => {
    setExpandedSite(site.name);
    Promise.all(
      site.sensors.map((sensor) =>
        fetchSensorReference(site.name, sensor.name).then((ref) => ({
          sensorName: sensor.name,
          ref,
        })),
      ),
    )
      .then((results) => {
        const siteRefs: Record<string, SensorReference> = {};
        results.forEach(({ sensorName, ref }) => {
          siteRefs[sensorName] = ref;
        });
        setReferenceData((prev) => ({ ...prev, [site.name]: siteRefs }));
      })
      .catch((error) => {
        console.error("Error fetching sensor references:", error);
      });
  };

  const collapseSite = () => {
    setExpandedSite(null);
  };

  const handleSiteChange = (updatedSite: Site) => {
    setSites((prevSites) =>
      prevSites.map((site) =>
        site.name === updatedSite.name ? updatedSite : site,
      ),
    );
  };

  const handleSortAndFilterChange = useCallback(
    (items: SortAndFilterableItem[]) => {
      const updatedSites = items
        .map((item) => sites.find((site) => site.name === item.id))
        .filter((site): site is Site => site !== undefined);
      setFilteredAndSortedSites(updatedSites);
    },
    [sites],
  );

  const sortAndFilterItems: SortAndFilterableItem[] = useMemo(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    return sites.map((site) => ({
      id: site.name,
      name: site.name,
      maximum: site.maximum.toString(),
      updated: site.updated?.toISOString() ?? "",
      status: site.updated && site.updated > oneDayAgo ? "active" : "inactive",
    }));
  }, [sites]);

  return (
    <div className="sites-admin-view">
      <div className="filter-sort-controls">
        <SortAndFilterSection
          selectableFilterOptions={selectableFilterOptions}
          sortOptions={sortOptions}
          items={sortAndFilterItems}
          onChange={handleSortAndFilterChange}
        />
      </div>

      {filteredAndSortedSites.map((site) => {
        return (
          <Card className="site-card" key={site.name}>
            <div className="main">
              <span className="site-icon">
                <FaSitemap />
              </span>
              <span className="site-name">{site.name}</span>

              <div className="actions">
                <Button
                  className="user-actions"
                  buttonStyle="outline"
                  onClick={() =>
                    expandedSite === site.name
                      ? collapseSite()
                      : expandSite(site)
                  }
                >
                  {expandedSite === site.name ? (
                    <BiCollapseVertical />
                  ) : (
                    <BiExpandVertical />
                  )}
                </Button>
              </div>
            </div>

            <div
              className={`site-details ${expandedSite === site.name ? "expanded" : ""}`}
            >
              <div className="event-date">
                <span className="icon">
                  <CiCalendar />
                </span>
                Eventdato: {site.eventDate.toLocaleDateString("nb-NO")}
              </div>
              <div>
                <span className="icon">
                  <LuArrowUpToLine />
                </span>
                Site maksimum: {site.maximum}
              </div>
              <div className="sensors-count">
                <span className="icon">
                  <MdNumbers />
                </span>
                Antall aktive sensorer: {site.sensors.length}
              </div>
              <div className="updated">
                <span className="icon">
                  <AiOutlineFileSync />
                </span>
                Siste oppdatering:{" "}
                {site.updated
                  ? `${site.updated.toLocaleDateString("nb-NO")} ${site.updated.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}`
                  : "Aldri"}
              </div>
            </div>

            {expandedSite === site.name && (
              <div className="expanded-section">
                {site.sensors.length > 0 && (
                  <div className="sensors-section">
                    <h4>Sensorer</h4>
                    <div className="sensors-list">
                      {site.sensors.map((sensor) => {
                        const siteRefs = referenceData[site.name];
                        const sensorRef = siteRefs?.[sensor.name];
                        return (
                          <div key={sensor.name} className="sensor-item">
                            <div className="sensor-info">
                              <span className="sensor-name">{sensor.name}</span>
                              <span
                                className="sensor-current"
                                title="Verdien som vises til besøkende på siten (Råverdi − Referanse)"
                              >
                                Nåværende (besøkende): {sensor.current}
                              </span>
                              <SensorReferenceField
                                siteName={site.name}
                                sensorName={sensor.name}
                                currentRaw={sensorRef?.currentRaw}
                                reference={sensorRef?.currentRawReference}
                                onUpdated={(newReference, newCurrent) => {
                                  setReferenceData((prev) => ({
                                    ...prev,
                                    [site.name]: {
                                      ...prev[site.name],
                                      [sensor.name]: {
                                        ...prev[site.name]?.[sensor.name],
                                        currentRawReference: newReference,
                                        current: newCurrent,
                                      },
                                    },
                                  }));
                                  setSites((prev) =>
                                    prev.map((s) =>
                                      s.name === site.name
                                        ? {
                                            ...s,
                                            sensors: s.sensors.map((se) =>
                                              se.name === sensor.name
                                                ? { ...se, current: newCurrent }
                                                : se,
                                            ),
                                          }
                                        : s,
                                    ),
                                  );
                                }}
                              />
                              <span className="sensor-updated">
                                Oppdatert:{" "}
                                {sensor.updated
                                  ? `${sensor.updated.toLocaleDateString("nb-NO")} ${sensor.updated.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}`
                                  : "Aldri"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <SitesAdminForm site={site} onChange={handleSiteChange} />
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default SitesAdmin;
